import React, { useState, useEffect, useContext, useCallback } from 'react';
import { 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Shield, 
  Mail, 
  Lock, 
  Server, 
  Eye, 
  Globe, 
  ChevronLeft,
  Award,
  Calendar,
  ExternalLink,
  Info,
  X,
  Filter,
  RefreshCw
} from 'lucide-react';

import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';
import { AssetsApi, Domain } from 'api-client';
import { getApiConfiguration } from '../api/utils';
import { AuthContext } from '../contexts/AuthContext';
import { useTeams } from '../contexts/TeamsContext';
import { useLoading } from '../hooks/useLoading';
import { FilterSelect } from '@/components/findings/filters/FilterSelect';

interface ScanAdvice {
    advice: string;
    effort: string;
    priority: string;
}

interface DomainScanData {
    dkim?: string;
    dkim_status?: string;
    dkim_advice?: ScanAdvice[];
    dmarc?: string;
    dmarc_status?: string;
    dmarc_advice?: ScanAdvice[];
    spf?: string;
    spf_status?: string;
    spf_advice?: ScanAdvice[];
    mx?: string[];
    mx_status?: string;
    mx_advice?: ScanAdvice[];
    ns?: string[];
    ns_status?: string;
    ns_advice?: ScanAdvice[];
    domain_advice?: ScanAdvice[];
    bimi_advice?: string[];
    tls_advice?: ScanAdvice[];
    tls_certificate?: {
        sni?: string;
        host?: string;
        port?: string;
        cipher?: string;
        serial?: string;
        issuer_cn?: string;
        issuer_dn?: string;
        not_after?: string;
        issuer_org?: string[];
        mismatched?: boolean;
        not_before?: string;
        subject_an?: string[];
        subject_cn?: string;
        subject_dn?: string;
        tls_version?: string;
        probe_status?: boolean;
        tls_connection?: string;
        fingerprint_hash?: {
            md5?: string;
            sha1?: string;
            sha256?: string;
        };
        wildcard_certificate?: boolean;
    };
    tls_certificate_status?: string;
    overall_status?: string;
    created_at?: string;
}

interface SubdomainStats {
    totalCount: number;
    activeCount: number;
    inactiveCount: number;
}

const StatusBadge = ({ status }: { status: string }) => {
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'configured':
            case 'valid':
            case 'secure':
                return 'text-emerald-500';
            case 'warning':
            case 'insecure':
            case 'quarantine':
                return 'text-yellow-500';
            case 'not_configured':
            case 'critical':
            case 'invalid':
            case 'expired':
            case 'none':
            case 'missing':
                return 'text-red-500';
            default:
                return 'text-gray-400';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'configured':
            case 'valid':
            case 'secure':
                return <CheckCircle size={16} className="text-emerald-500" />;
            case 'not_configured':
            case 'critical':
            case 'invalid':
            case 'expired':
            case 'none':
            case 'missing':
                return <X size={16} className="text-red-500" />;
            case 'warning':
            case 'insecure':
            case 'quarantine':
                return <AlertTriangle size={16} className="text-yellow-500" />;
            default:
                return null;
        }
    };

    return (
        <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium">
            {getStatusIcon(status)}
            <span className={getStatusColor(status)}>{status.replace('_', ' ')}</span>
        </span>
    );
};

const RiskScoreBadge = ({ score }: { score: string }) => {
    const getScoreColor = (score: string) => {
        switch (score.toLowerCase()) {
            case 'a':
            case 'a+':
                return 'bg-emerald-500/20 text-emerald-500 border border-emerald-500/30';
            case 'b':
                return 'bg-blue-500/20 text-blue-500 border border-blue-500/30';
            case 'c':
                return 'bg-yellow-500/20 text-yellow-500 border border-yellow-500/30';
            case 'd':
            case 'e':
                return 'bg-orange-500/20 text-orange-500 border border-orange-500/30';
            case 'f':
                return 'bg-red-500/20 text-red-500 border border-red-500/30';
            default:
                return 'bg-gray-700/30 text-gray-400 border border-gray-600/30';
        }
    };

    return (
        <span className={`inline-flex items-center justify-center w-12 h-12 rounded-full font-bold text-xl ${getScoreColor(score)}`}>
            {score.toUpperCase()}
        </span>
    );
};

const AdviceItem = ({ advice }: { advice: ScanAdvice }) => {
    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'high':
                return 'border-red-500';
            case 'medium':
                return 'border-yellow-500';
            case 'low':
                return 'border-blue-500';
            default:
                return 'border-gray-600';
        }
    };

    const getPriorityBadge = (priority: string) => {
        const colors: Record<string, string> = {
            high: 'bg-red-500/20 text-red-500 border border-red-500/30',
            medium: 'bg-yellow-500/20 text-yellow-500 border border-yellow-500/30',
            low: 'bg-blue-500/20 text-blue-500 border border-blue-500/30',
        };
  
        return (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[priority as keyof typeof colors] || 'bg-gray-700/30 text-gray-400 border border-gray-600/30'}`}>
                {priority}
            </span>
        );
    };

    const getEffortBadge = (effort: string) => {
        const colors: Record<string, string> = {
            high: 'bg-red-500/20 text-red-500 border border-red-500/30',
            medium: 'bg-yellow-500/20 text-yellow-500 border border-yellow-500/30',
            low: 'bg-emerald-500/20 text-emerald-500 border border-emerald-500/30',
        };
  
        return (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[effort as keyof typeof colors] || 'bg-gray-700/30 text-gray-400 border border-gray-600/30'}`}>
                effort: {effort}
            </span>
        );
    };

    return (
        <div className={`bg-gray-800/30 p-3 rounded-md border-l-4 ${getPriorityColor(advice.priority)} shadow-sm`}>
            <div className="flex items-start gap-2">
                <Info size={16} className="text-gray-400 mt-0.5" />
                <div className="flex-1">
                    <p className="text-sm text-white">{advice.advice}</p>
                    <div className="flex gap-2 mt-2">
                        {getPriorityBadge(advice.priority)}
                        {getEffortBadge(advice.effort)}
                    </div>
                </div>
            </div>
        </div>
    );
};

const SecurityConfigCard = (
    {
        title,
        status,
        icon: Icon,
        children
    }: { title: string; status: string; icon: any; children: React.ReactNode }) => {
    return (
        <div className="bg-gray-800/30 rounded-lg border border-gray-800/50 overflow-hidden">
            <div className="px-4 py-3 bg-gray-800/50 border-b border-gray-700/50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Icon size={20} className="text-gray-400" />
                    <h3 className="font-medium text-white">{title}</h3>
                </div>
                <StatusBadge status={status} />
            </div>
            <div className="p-4">
                {children}
            </div>
        </div>
    );
};

const DNSRecord = ({ type, name, data }: { type: string; name: string; data: string }) => (
    <div className="bg-gray-800/30 p-2 rounded text-sm mb-2">
        <div className="flex items-center gap-2">
            <span className="font-medium text-white min-w-16">{type}</span>
            <span className="text-gray-400 flex-1">{name}</span>
            <span className="text-gray-300">{data}</span>
        </div>
    </div>
);

const CertificateInfo = ({ certificate }: { certificate: DomainScanData['tls_certificate'] }) => {
    if (!certificate) return null;
  
    // Calculate expiry
    const getExpiryInfo = () => {
        if (!certificate.not_after) return null;
    
        const expiryDate = new Date(certificate.not_after);
        const now = new Date();
        const diffTime = expiryDate.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
        return {
            days: diffDays,
            isExpiring: diffDays <= 30 // Consider expiring if less than 30 days
        };
    };
  
    const expiryInfo = getExpiryInfo();
  
    return (
        <div>
            <div className={`mb-4 p-3 rounded-md ${expiryInfo && expiryInfo.isExpiring ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-gray-800/30'}`}>
                <div className="flex items-center gap-2">
                    <Clock size={16} className={expiryInfo && expiryInfo.isExpiring ? 'text-yellow-500' : 'text-gray-400'} />
                    <div>
                        <p className="text-sm font-medium text-white">Valid until: {certificate.not_after ? new Date(certificate.not_after).toISOString().split('T')[0] : 'N/A'}</p>
                        {expiryInfo && (
                            <p className="text-xs text-gray-400">
                                {expiryInfo.days <= 0
                                    ? 'Certificate has expired!'
                                    : expiryInfo.isExpiring
                                        ? `Expires in ${expiryInfo.days} days`
                                        : `Valid for ${expiryInfo.days} more days`}
                            </p>
                        )}
                    </div>
                </div>
            </div>
      
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <h4 className="text-sm font-medium text-white mb-2">Certificate Details</h4>
                    <table className="w-full text-sm">
                        <tbody>
                            <tr>
                                <td className="py-1 text-gray-400">Issuer</td>
                                <td className="py-1 text-white">{certificate.issuer_cn || 'N/A'}</td>
                            </tr>
                            <tr>
                                <td className="py-1 text-gray-400">Subject</td>
                                <td className="py-1 text-white">{certificate.subject_cn || 'N/A'}</td>
                            </tr>
                            <tr>
                                <td className="py-1 text-gray-400">Valid from</td>
                                <td className="py-1 text-white">{certificate.not_before ? new Date(certificate.not_before).toISOString().split('T')[0] : 'N/A'}</td>
                            </tr>
                            <tr>
                                <td className="py-1 text-gray-400">Serial</td>
                                <td className="py-1 font-mono text-xs text-gray-300">{certificate.serial || 'N/A'}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
        
                <div>
                    <h4 className="text-sm font-medium text-white mb-2">Security</h4>
                    <table className="w-full text-sm">
                        <tbody>
                            <tr>
                                <td className="py-1 text-gray-400">TLS Version</td>
                                <td className="py-1 text-white">{certificate.tls_version || 'N/A'}</td>
                            </tr>
                            <tr>
                                <td className="py-1 text-gray-400">Cipher</td>
                                <td className="py-1 text-white">{certificate.cipher || 'N/A'}</td>
                            </tr>
                            <tr>
                                <td className="py-1 text-gray-400">Wildcard</td>
                                <td className="py-1 text-white">{certificate.wildcard_certificate ? 'Yes' : 'No'}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
      
            {certificate.subject_an && certificate.subject_an.length > 0 && (
                <div className="mt-4">
                    <h4 className="text-sm font-medium text-white mb-2">Alternative Names</h4>
                    <div className="flex flex-wrap gap-2">
                        {certificate.subject_an.map((name, index) => (
                            <span key={index} className="bg-gray-800/50 text-gray-300 border border-gray-700 px-2 py-1 rounded text-xs">
                                {name}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const DomainDetailView = ({ domains, isLoading, setIsLoading }: { domains: Domain[]; isLoading: boolean; setIsLoading: (isLoading: boolean) => void }) => {
    const [domain, setDomain] = useState<Domain | null>(null);

    useEffect(() => {
        if (domains && domains.length > 0) {
            setDomain(domains[0]);
        }
        setIsLoading(false);
    }, [domains, setIsLoading]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="mt-2 text-gray-400">Loading domain security data...</p>
                </div>
            </div>
        );
    }

    if (!domain) {
        return (
            <div className="max-w-4xl mx-auto px-4 py-8">
                <div className="bg-red-500/10 text-red-500 p-4 rounded-md border border-red-500/30">
                    No domain data available.
                </div>
            </div>
        );
    }
    // ... rest of the code remains the same ...
    
    const parseLatestScan = (scanData: string | null | undefined): DomainScanData | null => {
        if (!scanData) return null;
    
        try {
            // If scanData is already an object, return it as is
            const parsed = typeof scanData === 'string' ? JSON.parse(scanData) : scanData;
            return parsed as DomainScanData;
        } catch (error) {
            console.error('Error parsing scan data:', error);
            return null;
        }
    }

    const parseSubdomainStats = (stats: string | null | undefined): SubdomainStats | null => {
        if (!stats) return null;
        
        try {
            const parsed = typeof stats === 'string' ? JSON.parse(stats) : stats;
            return parsed as SubdomainStats;
        } catch (error) {
            console.error('Error parsing subdomain stats:', error);
            return null;
        }
    };

    // const subdomainStats = parseSubdomainStats(domain?.subdomainStats);

    const scan = parseLatestScan(domain.latestScan);
  
    return (
        <div className="mx-auto px-4 py-6">
            <header className="mb-6">
                {/* <div className="flex items-center gap-2">
                    <a href="/" className="text-blue-500 hover:text-blue-400 flex items-center gap-1">
                        <ChevronLeft size={16} />
                        <span>Back to Dashboard</span>
                    </a>
                </div> */}
        
                <div className="flex items-center gap-6 mt-4">
                    <RiskScoreBadge score={domain.riskScore || ''} />
          
                    <div>
                        <h1 className="text-2xl font-bold text-white">{domain.domainName}</h1>
                        <div className="flex items-center gap-4 text-sm text-gray-400 mt-1">
                            <div className="flex items-center gap-1">
                                <Calendar size={14} className="text-gray-500" />
                                <span>Registered: {domain.registrationDate ? new Date(domain.registrationDate).toISOString().split('T')[0] : 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <Calendar size={14} className="text-gray-500" />
                                <span>Expires: {domain.expirationDate ? new Date(domain.expirationDate).toISOString().split('T')[0] : 'N/A'}</span>
                            </div>
                            <div>
                                <a href={`https://${domain.domainName}`} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-400 flex items-center gap-1">
                                    <ExternalLink size={14} />
                                    <span>Visit site</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </header>
        
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="col-span-1 md:col-span-2">
                    <div className="bg-[#0F1629] rounded-xl p-4 mb-6 border border-gray-800/50">
                        <h2 className="text-lg font-medium text-white mb-4">Security Overview</h2>
            
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="bg-gray-800/30 p-3 rounded-md">
                                <h3 className="text-sm font-medium text-white flex items-center gap-1 mb-2">
                                    <Mail size={16} className="text-gray-400" />
                                    Email Security
                                </h3>
                                <div className="space-y-2">
                                    <div className="flex justify-between text-xs">
                                        <span className="text-gray-400">DKIM</span>
                                        <StatusBadge status={scan?.dkim_status || 'unknown'} />
                                    </div>
                                    <div className="flex justify-between text-xs">
                                        <span className="text-gray-400">DMARC</span>
                                        <StatusBadge status={scan?.dmarc_status || 'unknown'} />
                                    </div>
                                    <div className="flex justify-between text-xs">
                                        <span className="text-gray-400">SPF</span>
                                        <StatusBadge status={scan?.spf_status || 'unknown'} />
                                    </div>
                                </div>
                            </div>
              
                            <div className="bg-gray-800/30 p-3 rounded-md">
                                <h3 className="text-sm font-medium text-white flex items-center gap-1 mb-2">
                                    <Globe size={16} className="text-gray-400" />
                                    DNS Security
                                </h3>
                                <div className="space-y-2">
                                    <div className="flex justify-between text-xs">
                                        <span className="text-gray-400">Name Servers</span>
                                        <StatusBadge status={scan?.ns_status || 'unknown'} />
                                    </div>
                                    <div className="flex justify-between text-xs">
                                        <span className="text-gray-400">MX Records</span>
                                        <StatusBadge status={scan?.mx_status || 'unknown'} />
                                    </div>
                                </div>
                            </div>
              
                            <div className="bg-gray-800/30 p-3 rounded-md">
                                <h3 className="text-sm font-medium text-white flex items-center gap-1 mb-2">
                                    <Lock size={16} className="text-gray-400" />
                                    TLS Certificate
                                </h3>
                                <div className="space-y-2">
                                    <div className="flex justify-between text-xs">
                                        <span className="text-gray-400">Status</span>
                                        <StatusBadge status={scan?.tls_certificate_status || 'unknown'} />
                                    </div>
                                    {scan?.tls_certificate && (
                                        <div className="flex justify-between text-xs">
                                            <span className="text-gray-400">Expiry</span>
                                            <span className="text-gray-300">
                                                {scan.tls_certificate.not_after ? new Date(scan.tls_certificate.not_after).toLocaleDateString() : 'N/A'}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
          
                    <div className="grid grid-cols-1 gap-6">
                        {/* Email Security Configurations */}
                        <div>
                            <h2 className="text-lg font-medium mb-4 text-white">Email Security Configuration</h2>
                            <div className="space-y-4">
                                <SecurityConfigCard title="DKIM Record" status={scan?.dkim_status || 'unknown'} icon={Mail}>
                                    {scan?.dkim ? (
                                        <div className="bg-gray-800/30 p-3 rounded font-mono text-xs overflow-auto text-white">
                                            {scan?.dkim}
                                        </div>
                                    ) : (
                                        <div className="text-gray-400 text-sm italic">No DKIM record configured</div>
                                    )}
                  
                                    {scan?.dkim_advice && scan?.dkim_advice.length > 0 && (
                                        <div className="mt-4 space-y-3">
                                            <h4 className="text-sm font-medium text-white">Recommendations</h4>
                                            {scan?.dkim_advice.map((advice, index) => (
                                                <AdviceItem key={index} advice={advice} />
                                            ))}
                                        </div>
                                    )}
                                </SecurityConfigCard>
                
                                <SecurityConfigCard title="DMARC Record" status={scan?.dmarc_status || 'unknown'} icon={Mail}>
                                    {scan?.dmarc ? (
                                        <div className="bg-gray-800/30 p-3 rounded font-mono text-xs overflow-auto text-white">
                                            {scan?.dmarc}
                                        </div>
                                    ) : (
                                        <div className="text-gray-400 text-sm italic">No DMARC record configured</div>
                                    )}
                  
                                    {scan?.dmarc_advice && scan?.dmarc_advice.length > 0 && (
                                        <div className="mt-4 space-y-3">
                                            <h4 className="text-sm font-medium text-white">Recommendations</h4>
                                            {scan?.dmarc_advice.map((advice, index) => (
                                                <AdviceItem key={index} advice={advice} />
                                            ))}
                                        </div>
                                    )}
                                </SecurityConfigCard>
                
                                <SecurityConfigCard title="SPF Record" status={scan?.spf_status || 'unknown'} icon={Mail}>
                                    {scan?.spf ? (
                                        <div className="bg-gray-800/30 p-3 rounded font-mono text-xs overflow-auto text-white">
                                            {scan?.spf}
                                        </div>
                                    ) : (
                                        <div className="text-gray-400 text-sm italic">No SPF record configured</div>
                                    )}
                  
                                    {scan?.spf_advice && scan?.spf_advice.length > 0 && (
                                        <div className="mt-4 space-y-3">
                                            <h4 className="text-sm font-medium text-white">Recommendations</h4>
                                            {scan?.spf_advice.map((advice, index) => (
                                                <AdviceItem key={index} advice={advice} />
                                            ))}
                                        </div>
                                    )}
                                </SecurityConfigCard>
                            </div>
                        </div>
            
                        {/* DNS Records */}
                        <div>
                            <h2 className="text-lg font-medium text-white mb-4">DNS Records</h2>
              
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <SecurityConfigCard title="MX Records" status={scan?.mx_status || 'unknown'} icon={Mail}>
                                    {scan?.mx && scan?.mx.length > 0 ? (
                                        <div className="space-y-2">
                                            {scan?.mx.map((record, index) => (
                                                <div key={index} className="bg-gray-800/30 p-2 rounded text-sm text-white">
                                                    {record}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-gray-400 text-sm italic">No MX records found</div>
                                    )}
                  
                                    {scan?.mx_advice && scan?.mx_advice.length > 0 && (
                                        <div className="mt-4 space-y-3">
                                            <h4 className="text-sm font-medium text-white">Recommendations</h4>
                                            {scan?.mx_advice.map((advice, index) => (
                                                <AdviceItem key={index} advice={advice} />
                                            ))}
                                        </div>
                                    )}
                                </SecurityConfigCard>
                
                                <SecurityConfigCard title="NS Records" status={scan?.ns_status || 'unknown'} icon={Server}>
                                    {scan?.ns && scan?.ns.length > 0 ? (
                                        <div className="space-y-2">
                                            {scan?.ns.map((record, index) => (
                                                <div key={index} className="bg-gray-800/30 p-2 rounded text-sm text-white">
                                                    {record}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-gray-400 text-sm italic">No NS records found</div>
                                    )}
                  
                                    {scan?.ns_advice && scan?.ns_advice.length > 0 && (
                                        <div className="mt-4 space-y-3">
                                            <h4 className="text-sm font-medium text-white">Recommendations</h4>
                                            {scan?.ns_advice.map((advice, index) => (
                                                <AdviceItem key={index} advice={advice} />
                                            ))}
                                        </div>
                                    )}
                                </SecurityConfigCard>
                            </div>
              
                            <div className="mt-6">
                                <SecurityConfigCard title="Other DNS Records" status="configured" icon={Server}>
                                    {domain?.dnsRecords && domain.dnsRecords.length > 0 ? (
                                        <div className="space-y-2">
                                            {domain.dnsRecords.map((record: any, index: number) => (
                                                <DNSRecord
                                                    key={index}
                                                    type={record.type}
                                                    name={record.name}
                                                    data={record.data}
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-gray-400 text-sm italic">No DNS records found</div>
                                    )}
                                </SecurityConfigCard>
                            </div>
                        </div>
            
                        {/* TLS Certificate */}
                        <div>
                            <h2 className="text-lg font-medium text-white mb-4">TLS Certificate</h2>
              
                            <SecurityConfigCard title="Certificate Details" status={scan?.tls_certificate_status || 'unknown'} icon={Lock}>
                                <CertificateInfo certificate={scan?.tls_certificate} />
                
                                {scan?.tls_advice && scan?.tls_advice.length > 0 && (
                                    <div className="mt-4 space-y-3">
                                        <h4 className="text-sm font-medium text-white">Recommendations</h4>
                                        {scan?.tls_advice.map((advice, index) => (
                                            <AdviceItem key={index} advice={advice} />
                                        ))}
                                    </div>
                                )}
                            </SecurityConfigCard>
                        </div>
                    </div>
                </div>
        
                <div className="col-span-1">
                    <div className="bg-[#0F1629] rounded-xl border border-gray-800/50">
                        <div className="px-4 py-3 bg-gray-800/50 border-b border-gray-700/50">
                            <h2 className="font-medium text-white">Domain Information</h2>
                        </div>
                        <div className="p-4">
                            <table className="w-full text-sm">
                                <tbody>
                                    <tr>
                                        <td className="py-2 text-gray-400">Registrar</td>
                                        <td className="py-2 text-white">{domain.registrar || 'N/A'}</td>
                                    </tr>
                                    <tr>
                                        <td className="py-2 text-gray-400">Registration Date</td>
                                        <td className="py-2 text-white">{domain.registrationDate ? new Date(domain.registrationDate).toLocaleDateString() : 'N/A'}</td>
                                    </tr>
                                    <tr>
                                        <td className="py-2 text-gray-400">Expiration Date</td>
                                        <td className="py-2 text-white">{domain.expirationDate ? new Date(domain.expirationDate).toLocaleDateString() : 'N/A'}</td>
                                    </tr>
                                    <tr>
                                        <td className="py-2 text-gray-400">Risk Score</td>
                                        <td className="py-2 text-white">{domain.riskScore?.toUpperCase() || 'N/A'}</td>
                                    </tr>
                                    {/* <tr>
                                        <td className="py-2 text-gray-400">Subdomains</td>
                                        <td className="py-2 text-white">{subdomainStats?.totalCount || 0}</td>
                                    </tr> */}
                                </tbody>
                            </table>
                        </div>
                    </div>
          
                    <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 mt-6">
                        <div className="px-4 py-3 bg-gray-800/50 border-b border-gray-700/50">
                            <h2 className="font-medium text-white">Domain Advice</h2>
                        </div>
                        <div className="p-4">
                            {scan?.domain_advice && scan?.domain_advice.length > 0 ? (
                                <div className="space-y-3">
                                    {scan?.domain_advice.map((advice, index) => (
                                        <AdviceItem key={index} advice={advice} />
                                    ))}
                                </div>
                            ) : (
                                <div className="text-gray-400 text-sm italic">No domain advice available</div>
                            )}
                        </div>
                    </div>
          
                    {scan?.bimi_advice && scan?.bimi_advice.length > 0 && (
                        <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 mt-6">
                            <div className="px-4 py-3 bg-gray-800/50 border-b border-gray-700/50">
                                <h2 className="font-medium text-white">BIMI Advice</h2>
                            </div>
                            <div className="p-4">
                                <div className="space-y-3">
                                    {scan?.bimi_advice.map((advice, index) => (
                                        <div key={index} className="text-sm text-white">{advice}</div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const SecurityCenterV2 = () => {

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { currentTeam } = useTeams();
    const { token } = useContext(AuthContext);
    const { isLoading, withLoading, setIsLoading } = useLoading();
  
    // State for domains from API
    const [domains, setDomains] = useState<Domain[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null);

    // Fetch domains from API
    const fetchDomains = useCallback(async () => {
        if (!currentTeam?.slug || !token) return;
        setIsLoading(true);
        try {
            const api = new AssetsApi(getApiConfiguration(token));
            const response = await api.domainsList({
                teamSlug: currentTeam.slug,
                page: 1,
            });
            console.log('Fetched domains:', JSON.parse(JSON.stringify(response.results)));
            setDomains(response.results);
            setIsLoading(false);
        } catch (err) {
            console.error('Error fetching domains:', err);
            setIsLoading(false);
            setError('Failed to fetch domains. Please try again later.');
        }
    }, [currentTeam?.slug, token]);

    React.useEffect(() => {
        fetchDomains();
    }, [fetchDomains]);
    
    // Check URL for domain parameter on initial load
    React.useEffect(() => {
        const domainParam = searchParams.get('domain');
        if (domainParam && domains.length > 0) {
            const matchedDomain = domains.find(d => d.domainName === domainParam);
            if (matchedDomain) {
                setSelectedDomain(matchedDomain);
            }
        }
    }, [domains, searchParams]);

    const handleScan = async () => {
        await withLoading(async () => {
            await fetchDomains();
            console.log('Scanning security...');
        });
    };

    // Parse scan data for domain card
    const parseLatestScan = (scanData: string | null | undefined): DomainScanData | null => {
        if (!scanData) return null;
    
        try {
            const parsed = typeof scanData === 'string' ? JSON.parse(scanData) : scanData;
            return parsed as DomainScanData;
        } catch (error) {
            console.error('Error parsing scan data:', error);
            return null;
        }
    };

    // Handle domain card click
    const handleDomainSelect = (domain: Domain) => {
        setSelectedDomain(domain);
        // Update URL with domain name
        setSearchParams({ domain: domain.domainName });
    };

    // Handle back button click
    const handleBackToList = () => {
        setSelectedDomain(null);
        // Remove domain from URL
        setSearchParams({});
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="mt-2 text-gray-400">Loading domain security data...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-4xl mx-auto px-4 py-8">
                <div className="bg-red-500/10 text-red-500 p-4 rounded-md border border-red-500/30">
                    {error}
                </div>
            </div>
        );
    }

    // If a domain is selected, show its details
    if (selectedDomain) {
        return (
            <div className="space-y-6 relative">
                <div className="flex items-center mb-6">
                    <Button 
                        variant="outline" 
                        onClick={handleBackToList}
                        className="text-blue-500 bg-transparent border-none hover:text-blue-400 hover:bg-transparent flex items-center gap-1"
                    >
                        <ChevronLeft size={16} />
                        <span>Back to All Domains</span>
                    </Button>
                </div>
                <DomainDetailView 
                    domains={[selectedDomain]} 
                    isLoading={false} 
                    setIsLoading={setIsLoading} 
                />
            </div>
        );
    }

    // Show domain cards if no domain is selected
    return (
        <div className="space-y-6 relative">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-white">Domain Security</h1>
                {/* <Button onClick={handleScan} disabled={isLoading}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                    {isLoading ? 'Refreshing...' : 'Refresh'}
                </Button> */}
            </div>

            {domains.length === 0 ? (
                <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-6 text-center">
                    <Globe className="h-12 w-12 text-gray-500 mx-auto mb-3" />
                    <h3 className="text-lg font-medium text-white mb-2">No Domains Found</h3>
                    <p className="text-gray-400 mb-4">You haven't added any domains to monitor yet.</p>
                    {/* <Button>Add Domain</Button> */}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {domains.map((domain) => {
                        const scan = parseLatestScan(domain.latestScan);
                        return (
                            <div 
                                key={domain.id} 
                                className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-4 hover:border-blue-500/50 hover:bg-[#0F1629]/80 transition-all cursor-pointer"
                                onClick={() => handleDomainSelect(domain)}
                            >
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="font-medium text-white truncate">{domain.domainName}</h3>
                                    <RiskScoreBadge score={domain.riskScore || ''} />
                                </div>
                                
                                <div className="grid grid-cols-2 gap-3 mb-4">
                                    <div className="bg-gray-800/30 p-2 rounded-md">
                                        <div className="text-xs text-gray-400 mb-1">Email Security</div>
                                        <div className="flex items-center">
                                            <StatusBadge status={scan?.overall_status || 'unknown'} />
                                        </div>
                                    </div>
                                    <div className="bg-gray-800/30 p-2 rounded-md">
                                        <div className="text-xs text-gray-400 mb-1">SSL/TLS</div>
                                        <div className="flex items-center">
                                            <StatusBadge status={scan?.tls_certificate_status || 'unknown'} />
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="flex items-center justify-between text-xs text-gray-400">
                                    <div className="flex items-center gap-1">
                                        <Calendar size={12} />
                                        <span>Expires: {domain.expirationDate ? new Date(domain.expirationDate).toISOString().split('T')[0] : 'N/A'}</span>
                                    </div>
                                    <div className="text-blue-500">View Details →</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default SecurityCenterV2;